/**
 * Access Requests API Endpoints — THE one client for pending changes to the
 * access graph. A request to let one person in and a request to widen a
 * document's audience are the same fact ("a proposed change awaiting a
 * decision"), so they are one collection, one row type and one decide pair.
 * `publishRequests.ts` is gone; its vocabulary (APPROVAL_PENDING, the resolved
 * approver list, the 202 pending body) lives here.
 *
 * Mirrors api-service-go/access_requests.go (the contract authority):
 *
 *   GET  /access-requests                  → the caller's OWN requests, any
 *        status (listMyAccessRequests, in access.ts)
 *   GET  /access-requests?decidable_by=me  → everything pending the caller may
 *        decide, whichever tier decides it
 *   POST /access-requests                  → 201 created row (self-grant only),
 *        or 409 REQUEST_ALREADY_PENDING
 *   POST /access-requests/{id}:approve     → 200 decided row, or 202 quorum-
 *        pending when the approver tier needs more sign-offs
 *   POST /access-requests/{id}:deny        → 200 decided row
 *
 * Approver-tier rows are never POSTed by a client: the share and publish
 * endpoints open them and answer 202 APPROVAL_PENDING with the row plus the
 * resolved approver list.
 *
 * Writes carry an `Idempotency-Key` (fresh UUID per invocation) so a
 * transport-level retry replays the cached result instead of tripping the
 * unique-pending index (create) or the already-decided 409 (decide). A user
 * re-click mints a NEW key — the load-bearing dedup is server-side.
 */

import { api } from '../client';
import type { Page } from '../types';
import { createLogger } from '@/utils/logger';
import { randomUUID } from '@/lib/uuid';

const logger = createLogger('AccessRequestsAPI');

/**
 * Wire code carried by the 409 when the caller already has an open request
 * for the resource. MUST equal the literal in api-service-go's
 * HandleCreateAccessRequest — keep the two in sync.
 */
export const REQUEST_ALREADY_PENDING_CODE = 'REQUEST_ALREADY_PENDING' as const;

/**
 * Wire code carried by the 409 when the caller ALREADY HOLDS the access they
 * are requesting (the server checks ACL before opening a request — a request
 * you don't need would only clutter an owner's inbox, or your own when you ARE
 * the owner). MUST equal `apicontract.ErrCodeAlreadyAuthorized` in
 * tricorder-go/apicontract/contract.go — the Go side's typed constant.
 */
export const ALREADY_AUTHORIZED_CODE = 'ALREADY_AUTHORIZED' as const;

/**
 * Wire code carried by the 202 when a share or a publish was turned into a
 * pending request instead of being written. MUST equal the Go
 * `errCodeApprovalPending` — keep the two in sync.
 */
export const APPROVAL_PENDING_CODE = 'APPROVAL_PENDING' as const;

/**
 * Wire code carried by the 409 when another decider settled the request first.
 * One literal for both tiers, because there is one decide surface.
 */
export const ALREADY_DECIDED_CODE = 'ALREADY_DECIDED' as const;

/** Requestable tiers — ownership is transferred, never requested. */
export type AccessRequestLevel = 'viewer' | 'editor';

/**
 * Lifecycle states. `denied` is the ONE terminal word for "the change was
 * refused" on the wire; which word a human reads is a rendering decision keyed
 * on `authority` (an author whose guideline was refused reads "rejected", a
 * nurse refused access reads "denied").
 */
export type AccessRequestStatus = 'pending' | 'approved' | 'denied';

/**
 * Which tier decides this row. `owner` = the resource's owner (a person asking
 * to be let in); `approver` = a clinical approver on the resource's category,
 * through a per-category quorum (an audience widening). Written once by the
 * path that opens the row; the authorization itself is still evaluated live at
 * decision time.
 */
export type RequestAuthority = 'owner' | 'approver';

/**
 * What the change would do: a GRANT to a principal, or a VISIBILITY scope on
 * the resource itself. A grant names a grantee and a level; a visibility change
 * names a target scope and neither.
 */
export type RequestChange = 'grant' | 'visibility';

/** Grant targets the ACL substrate understands (the access graph's words). */
export type GranteeType = 'user' | 'group' | 'node';

/** Levels a grant can carry. `demographics` is the patient-data narrow tier. */
export type RequestAccessLevel = 'viewer' | 'editor' | 'demographics';

/**
 * One recorded sign-off on the approver tier's quorum ledger: WHO approved and
 * WHEN, and nothing else — the ledger stores no display name, and resolving one
 * here would compete with the approver list beside it.
 */
export interface RequestSignoff {
  approver_id: string;
  signed_at: string;
}

/**
 * One pending-change row. Mirrors Go AccessRequestResponse.
 *
 * The enrichment fields (`resource_title`, `grantee_name`, `requester_name`,
 * `requester_name`, which falls back to the email) are resolved server-side for the list views so a row
 * renders WHO wants WHAT without a second read; they are absent when
 * resolution missed, and absent renders as absent.
 */
export interface AccessRequest {
  id: string;
  resource_type: string;
  resource_id: string;
  resource_title?: string;
  authority: RequestAuthority;
  change: RequestChange;
  grantee_type?: GranteeType;
  grantee_id?: string;
  grantee_name?: string;
  target_scope?: string;
  access_level?: RequestAccessLevel;
  justification?: string;
  /** Whoever OPENED the change — not necessarily whoever benefits from it. */
  requested_by: string;
  requester_name?: string;
  status: AccessRequestStatus;
  decided_by?: string;
  decided_at?: string;
  created_at: string;
}

/**
 * A row as it comes back from a LIST read: the request plus the sign-offs
 * recorded against it so far, so a decider can tell a change one signature
 * short from one nobody has touched.
 *
 * The trail rides the list row and NOT `AccessRequest` itself, because the
 * decide and gate paths build the row without reading the ledger. A required
 * `signoffs` on the base type would promise `[]` on a 202 body that in fact
 * carries no such key — a confidently wrong answer, where an absent key is
 * merely absent. Type a read as this ONLY where the server ran the list
 * projection.
 */
export interface AccessRequestListItem extends AccessRequest {
  /** Always present on a list row; `[]` when nobody has signed. */
  signoffs: RequestSignoff[];
}

/**
 * One resolved approver shown to the requester ("who will approve this?").
 * `via` names the authority tier: "category" (kind-scoped expert) or
 * "platform" (admin / delegated approver). Mirrors Go PublishApprover.
 */
export interface PublishApprover {
  principal_type: 'user' | 'group';
  principal_id: string;
  display_name: string;
  via: 'category' | 'platform';
}

/**
 * The 202 body the publish/share endpoints return when the change was gated
 * into a pending request. Approvers are resolved server-side BEFORE the row is
 * created, so the list is always populated.
 */
export interface ApprovalPending {
  code: typeof APPROVAL_PENDING_CODE;
  /** The created row — a gate-path projection, so it carries no `signoffs`. */
  request: AccessRequest;
  approvers: PublishApprover[];
  quorum: number;
}

/**
 * Discriminates the 202 APPROVAL_PENDING body from a normal 200/201 payload.
 * The api client returns parsed JSON for any 2xx without exposing the HTTP
 * status, so the wire `code` field is the discriminator.
 */
export function isApprovalPending(value: unknown): value is ApprovalPending {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as Record<string, unknown>).code === APPROVAL_PENDING_CODE
  );
}

/**
 * The 202 quorum-pending body from `:approve` on an approver-tier row: this
 * sign-off was recorded but more distinct approvals are needed before the
 * change executes. Discriminated from the decided row by `approvals`, which a
 * request row never carries.
 */
export interface QuorumPending {
  status: 'pending';
  approvals: number;
  quorum: number;
}

export function isQuorumPending(value: AccessRequest | QuorumPending): value is QuorumPending {
  return 'approvals' in value;
}

/** Fresh idempotency key per write — same helper shape as org.ts. */
function idem(): { headers: Record<string, string> } {
  return { headers: { 'Idempotency-Key': randomUUID() } };
}

/**
 * Ask the unit's owner for access. Throws ApiError with code
 * 'REQUEST_ALREADY_PENDING' (409) when the caller already has an open request.
 */
export async function createLocationAccessRequest(
  locationId: string,
  body: { access_level: AccessRequestLevel; justification?: string },
): Promise<AccessRequest> {
  try {
    logger.info('Creating access request', { locationId, accessLevel: body.access_level });
    const response = await api.post<AccessRequest>(
      '/access-requests',
      // A unit is a `node` in the access graph — the same word the returned
      // row's resource_type carries, so this is the vocabulary clients already
      // read, not a new one.
      { resource: { type: 'node', id: locationId }, ...body },
      idem(),
    );
    logger.info('Access request created', { locationId, id: response.id });
    return response;
  } catch (error) {
    logger.error('Failed to create access request', { locationId, error });
    throw error;
  }
}

/**
 * The unit OWNER's review queue (pending requests only). Non-owners get a
 * 403 — callers hide their section on that, it is not an app failure.
 */
export async function listLocationAccessRequests(
  locationId: string,
): Promise<AccessRequestListItem[]> {
  try {
    // decidable_by=me states the PERSPECTIVE. Without it a resource filter is
    // ambiguous — it reads as "this unit's queue" to a lead and "my request for
    // it" to a nurse — and the server refuses to guess.
    const items = await api.getAllItems<Page<AccessRequestListItem>>(
      `/access-requests?decidable_by=me&resource_type=node&resource_id=${encodeURIComponent(locationId)}`,
    );
    logger.debug('Access requests listed', { locationId, count: items.length });
    return items;
  } catch (error) {
    logger.error('Failed to list access requests', { locationId, error });
    throw error;
  }
}

/**
 * The caller's whole decidable queue: every pending change they may settle,
 * across resources and across BOTH tiers (a grant they own, a publish they
 * approve). One feed, because it is one collection — authority filtering is
 * server-side and each row names its tier in `authority`.
 */
export async function listAccessRequestInbox(): Promise<{
  requests: AccessRequestListItem[];
  /** The response's `Link: rel="next"`: more pending than this page shows. */
  next: string | null;
}> {
  try {
    const response = await api.getPage<Page<AccessRequestListItem>>(
      '/access-requests?decidable_by=me',
    );
    logger.debug('Access-request inbox listed', {
      count: response.items.length,
      next: response.next,
    });
    return { requests: response.items, next: response.next };
  } catch (error) {
    logger.error('Failed to list access-request inbox', { error });
    throw error;
  }
}

/**
 * Decide a request. The custom method is a COLON on the item, not a sub-path:
 * approving is a verb on this request, not a child resource of it.
 *
 * Approve on an owner-tier row writes the real grant server-side before the
 * 200 — refetch the members panel after success. Approve on an approver-tier
 * row can instead answer 202 quorum-pending, meaning this sign-off landed but
 * the change has not executed and the row stays in the inbox.
 *
 * Throws ApiError with code 'ALREADY_DECIDED' (409) when another decider got
 * there first.
 */
export async function decideAccessRequest(
  id: string,
  decision: 'approve' | 'deny',
): Promise<AccessRequest | QuorumPending> {
  try {
    logger.info('Deciding access request', { id, decision });
    const response = await api.post<AccessRequest | QuorumPending>(
      `/access-requests/${encodeURIComponent(id)}:${decision}`,
      undefined, // handler reads no request body
      idem(),
    );
    logger.info('Access request decided', { id, decision });
    return response;
  } catch (error) {
    logger.error('Failed to decide access request', { id, decision, error });
    throw error;
  }
}
